import React, { useState, useEffect } from 'react';
import { 
  Box, Container, Typography, TextField, Button, 
  CircularProgress, Alert, Checkbox, 
  Grid, Card, CardContent, IconButton, Chip, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  ToggleButton, ToggleButtonGroup, FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { 
  Person, Apartment, LockOutlined, HelpOutline, 
  EventNoteOutlined, ContentCopyOutlined, HomeOutlined, 
  InfoOutlined, MailOutline, PhoneOutlined, CheckCircle, 
  EditOutlined, Close, ArrowBackIosNew, ArrowForwardIos, 
  ArrowForward, DeleteOutline, Star, StarBorder, CloudUpload, 
  VerifiedUserOutlined, QrCode2, SupportAgentOutlined,
  Visibility, PictureAsPdf, ZoomIn
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MediaService } from '../../api/services/mediaService';
import { getCookie } from '../../utils/cookies';
import { SUPPORTED_PHONE_CODES } from '../../utils/phoneValidation';

const createEmptyGuest = (isPrimary = false, defaultName = '', defaultEmail = '', defaultPhone = '', guestType = 'Adult', isIndian = true) => {
  const rawPhone = defaultPhone || '';
  const pMatch = rawPhone.match(/^(\+\d{1,4})\s*(.+)$/);
  const phoneCode = pMatch ? pMatch[1] : (isIndian ? '+91' : '+971');
  const phone = pMatch ? pMatch[2].replace(/\D/g, '') : (rawPhone.startsWith('+') ? rawPhone.replace(/^\+\d{1,4}\s*/, '').replace(/\D/g, '') : rawPhone);
  return {
    id: undefined,
    guestId: undefined,
    isPrimary,
    isIndian,
    guestType,
    title: '',
    name: defaultName,
    email: defaultEmail,
    phoneCode,
    phone,
    mobilePhoneCode: isIndian ? '+91' : '+971',
    mobile: '',
    gender: '',
    address: '',
    zip: '',
    country: isIndian ? 'India' : '',
    state: '',
    city: '',
    idProof: '',
    idType: isIndian ? 'Aadhar' : 'Passport',
    issuingCountry: '',
    issuingCity: '',
    idExpiryDate: '',
    idDocumentUrl: '',
    birthDate: '',
    birthCountry: isIndian ? 'India' : '',
    nationality: isIndian ? 'Indian' : ''
  };
};

const getApiBaseUrl = () => {
  let url = (process.env.REACT_APP_API_BASE_URL_PMS || 'http://localhost:3000').replace(/\/+$/, '');
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && url.startsWith('http://') && !url.includes('localhost')) {
    url = url.replace('http://', 'https://');
  }
  return url;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  if (typeof dateStr === 'string') {
    const datePart = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
    const parts = datePart.split('-');
    if (parts.length === 3 && parts[0].length === 4) {
      const [y, m, d] = parts;
      return `${d.padStart(2, '0')}/${m.padStart(2, '0')}/${y}`;
    }
  }
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return String(dateStr);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
};

const GuestWebCheckin = () => {
  const { token: rawTokenParam } = useParams();
  const token = (rawTokenParam || '').split('/')[0].trim();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [summary, setSummary] = useState(null);
  
  const [roomsData, setRoomsData] = useState([]);
  const [uploadingMap, setUploadingMap] = useState({});
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [activeModalGuest, setActiveModalGuest] = useState(null);
  const [copied, setCopied] = useState(false);
  const [previewImageUrl, setPreviewImageUrl] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      // Mock demo preview only when explicitly requesting token === 'demo'
      if (token === 'demo') {
        const demoData = {
          bookingId: 'BK-00006',
          hotelName: 'Grand Palace Resort & Suites',
          hotelPhone: '+971 4 800 2473',
          checkIn: '2026-10-29T14:00:00',
          checkOut: '2026-10-30T11:00:00',
          checkInTime: '14:00 hrs',
          checkOutTime: '11:00 hrs',
          checkInStatus: 'Confirmed',
          status: 'Confirmed',
          guest: {
            name: 'Niyas K',
            email: 'niyas@toobler.com',
            phone: '+9717894561230'
          },
          rooms: [
            {
              id: 'room-0',
              roomTypeName: 'Deluxe King Suite',
              wing: 'Ocean Wing',
              floor: '4th Floor',
              adultsCount: 1,
              kidsCount: 0,
              guests: [
                {
                  id: 'g-0',
                  isPrimary: true,
                  isIndian: true,
                  guestType: 'Adult',
                  name: 'Niyas K',
                  email: 'niyas@toobler.com',
                  phone: '+9717894561230',
                  idType: 'Aadhar',
                  idProof: ''
                }
              ]
            }
          ]
        };
        setSummary(demoData);
        setRoomsData(demoData.rooms);
        setTermsAccepted(true);
        setLoading(false);
        return;
      }

      try {
        const baseUrl = getApiBaseUrl();
        const response = await axios.get(`${baseUrl}/api/v1/public/guest/checkin/${token}`);
        if (response.data?.status === 'success') {
          const data = response.data.data;
          setSummary(data);
          if (data.checkInStatus === 'Pre-Checked In') {
            setSuccess(true);
          }
          
          // Populate initial rooms and guests from real API data
          if (data.rooms && data.rooms.length > 0) {
            const initialRooms = data.rooms.map((room, rIdx) => {
              const adultsCount = Number(room.adultsCount) || 1;
              const kidsCount = Number(room.kidsCount) || 0;
              const expectedTotal = Math.max(1, adultsCount + kidsCount);

              const existingGuests = (room.guests && room.guests.length > 0)
                ? room.guests.map((g, gIdx) => {
                    const isIndian = g.isIndian !== undefined
                      ? Boolean(g.isIndian)
                      : (g.country ? (g.country.toLowerCase() === 'india' || g.nationality?.toLowerCase() === 'indian') : (g.phone ? g.phone.startsWith('+91') : true));
                    const rawPhone = g.phone || '';
                    const pMatch = rawPhone.match(/^(\+\d{1,4})\s*(.+)$/);
                    const phoneCode = g.phoneCode || (pMatch ? pMatch[1] : (isIndian ? '+91' : '+971'));
                    const phone = pMatch ? pMatch[2].replace(/\D/g, '') : (rawPhone.startsWith('+') ? rawPhone.replace(/^\+\d{1,4}\s*/, '').replace(/\D/g, '') : rawPhone);

                    const rawMobile = g.mobile || '';
                    const mMatch = rawMobile.match(/^(\+\d{1,4})\s*(.+)$/);
                    const mobilePhoneCode = g.mobilePhoneCode || (mMatch ? mMatch[1] : (isIndian ? '+91' : '+971'));
                    const mobile = mMatch ? mMatch[2].replace(/\D/g, '') : (rawMobile.startsWith('+') ? rawMobile.replace(/^\+\d{1,4}\s*/, '').replace(/\D/g, '') : rawMobile);

                    return {
                      id: g.id,
                      guestId: g.guestId,
                      isPrimary: Boolean(g.isPrimary || (rIdx === 0 && gIdx === 0)),
                      isIndian,
                      guestType: g.guestType || 'Adult',
                      title: g.title || '',
                      name: g.name || '',
                      email: g.email || '',
                      phoneCode,
                      phone,
                      mobilePhoneCode,
                      mobile,
                      gender: g.gender || '',
                      address: g.address || '',
                      zip: g.zip || '',
                      country: g.country || (isIndian ? 'India' : ''),
                      state: g.state || '',
                      city: g.city || '',
                      idProof: g.idProof || '',
                      idType: g.idType || (isIndian ? 'Aadhar' : 'Passport'),
                      issuingCountry: g.issuingCountry || '',
                      issuingCity: g.issuingCity || '',
                      idExpiryDate: g.idExpiryDate ? g.idExpiryDate.split('T')[0] : '',
                      idDocumentUrl: (g.idDocumentUrl && g.idDocumentUrl !== 'null' && g.idDocumentUrl !== 'undefined') ? g.idDocumentUrl : '',
                      birthDate: g.birthDate ? g.birthDate.split('T')[0] : '',
                      birthCountry: g.birthCountry || (isIndian ? 'India' : ''),
                      nationality: g.nationality || (isIndian ? 'Indian' : '')
                    };
                  })
                : [
                    createEmptyGuest(
                      rIdx === 0,
                      rIdx === 0 ? (data.guest?.name || '') : '',
                      rIdx === 0 ? (data.guest?.email || '') : '',
                      rIdx === 0 ? (data.guest?.phone || '') : '',
                      'Adult',
                      true
                    )
                  ];

              // Automatically add guest forms for all guests added to the reservation room
              while (existingGuests.length < expectedTotal) {
                const isKid = existingGuests.length >= adultsCount;
                existingGuests.push(createEmptyGuest(false, '', '', '', isKid ? 'Child' : 'Adult'));
              }

              return {
                id: room.id || `room-${rIdx}`,
                roomTypeName: room.roomTypeName || `Room ${rIdx + 1}`,
                roomNumber: room.roomNumber || '',
                wing: room.wing || '',
                floor: room.floor || '',
                adultsCount,
                kidsCount,
                guests: existingGuests
              };
            });
            setRoomsData(initialRooms);
          } else {
            // Default room fallback from real data
            const fallbackAdults = Number(data.adults || data.adultsCount) || 1;
            const fallbackKids = Number(data.kids || data.kidsCount) || 0;
            const fallbackTotal = Math.max(1, fallbackAdults + fallbackKids);
            const defaultGuests = [
              createEmptyGuest(
                true,
                data.guest?.name || '',
                data.guest?.email || '',
                data.guest?.phone || '',
                'Adult'
              )
            ];
            while (defaultGuests.length < fallbackTotal) {
              const isKid = defaultGuests.length >= fallbackAdults;
              defaultGuests.push(createEmptyGuest(false, '', '', '', isKid ? 'Child' : 'Adult'));
            }
            setRoomsData([{
              id: 'room-0',
              roomTypeName: data.roomTypeName || 'Assigned Room',
              roomNumber: data.roomNumber || '',
              wing: '',
              floor: '',
              adultsCount: fallbackAdults,
              kidsCount: fallbackKids,
              guests: defaultGuests
            }]);
          }
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Invalid or expired check-in link.');
      } finally {
        setLoading(false);
      }
    };
    if (token) {
      fetchSummary();
    } else {
      setLoading(false);
      setError('Check-in token is missing from the link.');
    }
  }, [token]);

  const handleCopyBookingId = (e) => {
    e?.stopPropagation();
    const idToCopy = summary?.bookingId || '';
    if (idToCopy && navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(idToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleGuestChange = (roomIndex, guestIndex, field, value) => {
    setRoomsData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy[roomIndex].guests[guestIndex][field] = value;
      return copy;
    });
  };

  const handleSetPrimaryGuest = (roomIndex, guestIndex) => {
    setRoomsData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy.forEach((r, rIdx) => {
        r.guests.forEach((g, gIdx) => {
          g.isPrimary = (rIdx === roomIndex && gIdx === guestIndex);
        });
      });
      return copy;
    });
  };

  const handleFileUpload = async (event, roomIndex, guestIndex) => {
    const file = event.target?.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setError('Selected file exceeds the 5MB size limit.');
      return;
    }

    const key = `${roomIndex}_${guestIndex}`;
    setUploadingMap(prev => ({ ...prev, [key]: true }));
    setError(null);

    try {
      let url = '';
      let uploadedFileName = file.name;
      const baseUrl = getApiBaseUrl();
      const formData = new FormData();
      formData.append('file', file);

      // 1. Send uploaded file to the /public/guest/upload API endpoint with token in headers
      try {
        const headers = {};
        if (token && token !== 'demo') {
          headers['Authorization'] = `Bearer ${token}`;
        }

        let res;
        try {
          res = await axios.post(`${baseUrl}/api/v1/public/guest/upload`, formData, { headers });
        } catch (v1Err) {
          if (v1Err.response?.status === 404) {
            res = await axios.post(`${baseUrl}/public/guest/upload`, formData, { headers });
          } else {
            throw v1Err;
          }
        }

        const uploadRes = res.data?.data || res.data || res;
        url = uploadRes?.url ||
              uploadRes?.fileUrl ||
              uploadRes?.publicUrl ||
              uploadRes?.location ||
              uploadRes?.Location ||
              uploadRes?.path ||
              uploadRes?.file?.url ||
              uploadRes?.file?.location ||
              uploadRes?.file?.path ||
              (typeof uploadRes === 'string' ? uploadRes : '');

        uploadedFileName = uploadRes?.fileName ||
                           uploadRes?.filename ||
                           uploadRes?.originalName ||
                           uploadRes?.originalname ||
                           uploadRes?.name ||
                           file.name;
      } catch (uploadErr) {
        console.warn('/public/guest/upload endpoint failed, falling back to local file reader:', uploadErr);
        // Fallback to Data URL so check-in workflow is never blocked in demo/offline sessions
        url = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      }

      if (!url) {
        throw new Error('Upload succeeded but no file URL was returned.');
      }

      // 2. Take upload response URL and name and store it in guest state to be displayed and passed in payload
      setRoomsData(prev => {
        const copy = JSON.parse(JSON.stringify(prev));
        copy[roomIndex].guests[guestIndex].idDocumentUrl = url;
        copy[roomIndex].guests[guestIndex].idDocumentName = uploadedFileName;
        return copy;
      });
    } catch (err) {
      console.error('ID document upload failed:', err);
      const errorMsg = err?.response?.data?.message || err?.message || 'Failed to upload ID document. Please try again.';
      setError(errorMsg);
    } finally {
      setUploadingMap(prev => ({ ...prev, [key]: false }));
      if (event.target) event.target.value = '';
    }
  };

  const handleRemoveFile = (roomIndex, guestIndex) => {
    setRoomsData(prev => {
      const copy = JSON.parse(JSON.stringify(prev));
      copy[roomIndex].guests[guestIndex].idDocumentUrl = '';
      copy[roomIndex].guests[guestIndex].idDocumentName = '';
      return copy;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    let primaryGuest = null;

    for (const room of roomsData) {
      for (const guest of room.guests) {
        if (guest.isPrimary) {
          primaryGuest = guest;
        }
      }
    }

    if (!primaryGuest && roomsData[0]?.guests[0]) {
      primaryGuest = roomsData[0].guests[0];
      primaryGuest.isPrimary = true;
    }

    if (!primaryGuest?.name || !primaryGuest.name.trim()) {
      setError('Please provide the Full Name for the primary guest checking in.');
      return;
    }

    if (!termsAccepted) {
      setError('Please agree to the Hotel Terms & Conditions before completing registration.');
      return;
    }

    const payload = {
      title: primaryGuest?.title || '',
      name: primaryGuest?.name || '',
      email: primaryGuest?.email || '',
      phone: primaryGuest?.phone ? (primaryGuest.phone.startsWith('+') ? primaryGuest.phone : `${primaryGuest.phoneCode || (primaryGuest.isIndian ? '+91' : '+971')} ${primaryGuest.phone}`) : '',
      phoneCode: primaryGuest?.phoneCode || (primaryGuest.isIndian ? '+91' : '+971'),
      phoneNumber: primaryGuest?.phone || '',
      mobile: primaryGuest?.mobile ? (primaryGuest.mobile.startsWith('+') ? primaryGuest.mobile : `${primaryGuest.mobilePhoneCode || (primaryGuest.isIndian ? '+91' : '+971')} ${primaryGuest.mobile}`) : '',
      mobilePhoneCode: primaryGuest?.mobilePhoneCode || (primaryGuest.isIndian ? '+91' : '+971'),
      mobileNumber: primaryGuest?.mobile || '',
      gender: primaryGuest?.gender || '',
      address: primaryGuest?.address || '',
      zip: primaryGuest?.zip || '',
      country: primaryGuest?.isIndian ? 'India' : (primaryGuest?.country || ''),
      state: primaryGuest?.state || '',
      city: primaryGuest?.city || '',
      idProof: primaryGuest?.idProof || '',
      idType: primaryGuest?.idType || (primaryGuest?.isIndian ? 'Aadhar' : 'Passport'),
      issuingCountry: primaryGuest?.issuingCountry || '',
      issuingCity: primaryGuest?.issuingCity || '',
      idExpiryDate: primaryGuest?.idExpiryDate || '',
      idDocumentUrl: primaryGuest?.idDocumentUrl || '',
      birthDate: primaryGuest?.birthDate || '',
      birthCountry: primaryGuest?.isIndian ? 'India' : (primaryGuest?.birthCountry || ''),
      nationality: primaryGuest?.isIndian ? 'Indian' : (primaryGuest?.nationality || ''),
      rooms: roomsData.map(room => ({
        ...room,
        guests: room.guests.map(g => ({
          ...g,
          phone: g.phone ? (g.phone.startsWith('+') ? g.phone : `${g.phoneCode || (g.isIndian ? '+91' : '+971')} ${g.phone}`) : '',
          phoneCode: g.phoneCode || (g.isIndian ? '+91' : '+971'),
          phoneNumber: g.phone || '',
          mobile: g.mobile ? (g.mobile.startsWith('+') ? g.mobile : `${g.mobilePhoneCode || (g.isIndian ? '+91' : '+971')} ${g.mobile}`) : '',
          mobilePhoneCode: g.mobilePhoneCode || (g.isIndian ? '+91' : '+971'),
          mobileNumber: g.mobile || '',
          country: g.isIndian ? (g.country || 'India') : g.country,
          nationality: g.isIndian ? (g.nationality || 'Indian') : g.nationality,
          city: g.isIndian ? (g.city || '') : g.city,
          birthCountry: g.isIndian ? (g.birthCountry || 'India') : g.birthCountry,
          idType: g.idType || (g.isIndian ? 'Aadhar' : 'Passport'),
          idDocumentUrl: g.idDocumentUrl || ''
        }))
      }))
    };

    setSubmitting(true);
    try {
      const baseUrl = getApiBaseUrl();
      await axios.post(`${baseUrl}/api/v1/public/guest/checkin/${token}/submit`, payload);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit check-in. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8fafc' }}>
        <CircularProgress size={48} sx={{ color: '#4f46e5' }} />
      </Box>
    );
  }

  if (error && !summary) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Container maxWidth="sm">
          <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>
        </Container>
      </Box>
    );
  }

  if (success) {
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
        <Container maxWidth="sm">
          <Card sx={{ textAlign: 'center', py: 6, px: 4, borderRadius: '20px', border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
            <Box sx={{ width: 72, height: 72, borderRadius: '50%', bgcolor: '#ecfdf5', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', mb: 3 }}>
              <CheckCircle sx={{ fontSize: 44 }} />
            </Box>
            <Typography variant="h4" fontWeight="800" color="#0f172a" gutterBottom sx={{ fontSize: '1.75rem' }}>
              You're all set!
            </Typography>
            <Typography variant="body1" color="#64748b" sx={{ mb: 2, fontSize: '0.95rem' }}>
              Your pre-arrival registration is complete for all guests. Simply pick up your room key at the front desk upon arrival.
            </Typography>
          </Card>
        </Container>
      </Box>
    );
  }

  const totalAdults = summary?.rooms?.reduce((acc, r) => acc + (Number(r.adultsCount) || 1), 0) || 1;
  const totalKids = summary?.rooms?.reduce((acc, r) => acc + (Number(r.kidsCount) || 0), 0) || 0;
  const totalGuests = totalAdults + totalKids;
  const hotelName = summary?.hotelName || summary?.propertyName || 'Grand Palace Resort & Suites';
  const bookingId = summary?.bookingId || '';
  const checkInDateFormatted = formatDate(summary?.checkIn);
  const checkOutDateFormatted = formatDate(summary?.checkOut);
  const checkInTimeText = summary?.checkInTime ? `From ${summary.checkInTime}` : 'From 14:00 hrs';
  const checkOutTimeText = summary?.checkOutTime ? `Until ${summary.checkOutTime}` : 'Until 11:00 hrs';
  const roomNamesDisplay = (summary?.rooms && summary.rooms.length > 0)
    ? summary.rooms.map(r => r.roomTypeName || r.roomType).filter(Boolean).join(', ')
    : '';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', display: 'flex', flexDirection: 'column' }}>
      {/* Top Navbar */}
      <Box
        component="header"
        sx={{
          bgcolor: '#ffffff',
          borderBottom: '1px solid #e5e7eb',
          px: { xs: 2, sm: 4, md: 6 },
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 1.5
        }}
      >
        {/* Brand / Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              bgcolor: '#4f46e5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#ffffff',
              flexShrink: 0
            }}
          >
            <Apartment sx={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a', lineHeight: 1.2 }}>
              {hotelName}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.2 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#10b981', display: 'inline-block' }} />
              <Typography sx={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>
                Official Digital Concierge
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Security badge and help */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 } }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              bgcolor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '9999px',
              px: 1.5,
              py: 0.5
            }}
          >
            <LockOutlined sx={{ fontSize: 14, color: '#64748b', mr: 0.75 }} />
            <Typography sx={{ fontSize: '0.75rem', color: '#475569', fontWeight: 500 }}>
              256–bit Encrypted Check-in
            </Typography>
          </Box>

          <Box
            component="a"
            href={`tel:${summary?.hotelPhone || '+97148002473'}`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              color: '#4f46e5',
              textDecoration: 'none',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
              '&:hover': { color: '#4338ca' }
            }}
          >
            <HelpOutline sx={{ fontSize: 16, mr: 0.5 }} />
            Need Help?
          </Box>
        </Box>
      </Box>

      {/* Main Content Area */}
      <Box sx={{ flex: 1, py: { xs: 4, sm: 5 }, px: { xs: 2, sm: 3 } }}>
        <Container maxWidth="md" sx={{ maxWidth: '860px !important' }}>
          {/* Header Title Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              sx={{
                display: 'inline-block',
                bgcolor: '#ede9fe',
                color: '#4f46e5',
                borderRadius: '9999px',
                px: 2,
                py: 0.5,
                fontSize: '0.75rem',
                fontWeight: 600,
                mb: 1.5
              }}
            >
              Express Arrival Portal
            </Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                fontSize: { xs: '1.75rem', sm: '2.1rem' },
                color: '#0f172a',
                letterSpacing: '-0.02em',
                mb: 1
              }}
            >
              Fast-Track Your Stay
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                fontSize: '0.9rem',
                maxWidth: 540,
                mx: 'auto'
              }}
            >
              Register all guests for each assigned room in advance and skip the queue at reception upon arrival.
            </Typography>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
              {error}
            </Alert>
          )}

          {/* Reservation Summary Card */}
          <Card
            sx={{
              bgcolor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '16px',
              p: { xs: 2.5, sm: 3.5 },
              mb: 3,
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.03)'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    bgcolor: '#ede9fe',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <EventNoteOutlined sx={{ color: '#4f46e5', fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#0f172a' }}>
                  Reservation Summary
                </Typography>
              </Box>

              <Box
                sx={{
                  bgcolor: '#ecfdf5',
                  border: '1px solid #a7f3d0',
                  color: '#059669',
                  borderRadius: '9999px',
                  px: 1.5,
                  py: 0.4,
                  fontSize: '0.78rem',
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75
                }}
              >
                ● {summary?.status || summary?.checkInStatus || 'Confirmed'}
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={6} md={3}>
                <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.5 }}>
                  BOOKING ID
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                    {bookingId || '-'}
                  </Typography>
                  {bookingId && (
                    <Tooltip title={copied ? 'Copied!' : 'Copy Booking ID'}>
                      <IconButton
                        size="small"
                        onClick={handleCopyBookingId}
                        sx={{ p: 0.25, ml: 0.5, color: '#94a3b8', '&:hover': { color: '#4f46e5' } }}
                      >
                        <ContentCopyOutlined sx={{ fontSize: 15 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.5 }}>
                  CHECK-IN
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                  {checkInDateFormatted || '-'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.25 }}>
                  {checkInTimeText}
                </Typography>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.5 }}>
                  CHECK-OUT
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                  {checkOutDateFormatted || '-'}
                </Typography>
                <Typography sx={{ fontSize: '0.75rem', color: '#64748b', mt: 0.25 }}>
                  {checkOutTimeText}
                </Typography>
              </Grid>

              <Grid item xs={6} md={3}>
                <Typography sx={{ color: '#64748b', fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 0.5 }}>
                  ROOM(S)
                </Typography>
                <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
                  {roomNamesDisplay || 'Assigned Room'}
                </Typography>
                <Typography sx={{ fontSize: '0.78rem', color: '#4f46e5', fontWeight: 600, mt: 0.25 }}>
                  {totalGuests} Guest{totalGuests > 1 ? 's' : ''} ({totalAdults} Adult{totalAdults > 1 ? 's' : ''}{totalKids > 0 ? `, ${totalKids} Child${totalKids > 1 ? 'ren' : ''}` : ''})
                </Typography>
              </Grid>
            </Grid>
          </Card>

          {/* Registration Form */}
          <form onSubmit={handleSubmit}>
            {roomsData.map((room, rIdx) => (
              <Card
                key={room.id || rIdx}
                sx={{
                  bgcolor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  mb: 3,
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.03)'
                }}
              >
                {/* Room Card Dark Header */}
                <Box
                  sx={{
                    bgcolor: '#0f172a',
                    px: { xs: 2, sm: 3 },
                    py: 1.75,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    color: '#ffffff'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '8px',
                        bgcolor: 'rgba(255, 255, 255, 0.08)',
                        border: '1px solid rgba(255, 255, 255, 0.12)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <HomeOutlined sx={{ color: '#ffffff', fontSize: 20 }} />
                    </Box>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#ffffff', lineHeight: 1.2 }}>
                        Room {rIdx + 1}: {room.roomTypeName || room.roomType || `Room ${rIdx + 1}`}
                      </Typography>
                      {(room.wing || room.floor || room.roomNumber) && (
                        <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.2 }}>
                          {[
                            room.wing,
                            room.floor ? (String(room.floor).toLowerCase().includes('floor') ? room.floor : `${room.floor} Floor`) : null,
                            room.roomNumber ? `Room ${room.roomNumber}` : null
                          ].filter(Boolean).join(' • ')}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      borderRadius: '9999px',
                      px: 1.75,
                      py: 0.4,
                      color: '#cbd5e1',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    {room.guests.length} Guest Form ({room.adultsCount || 1} Adult{(room.adultsCount || 1) > 1 ? 's' : ''}{(room.kidsCount || 0) > 0 ? `, ${room.kidsCount} Child${room.kidsCount > 1 ? 'ren' : ''}` : ''})
                  </Box>
                </Box>

                {/* Room Body */}
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  {/* Occupancy info box */}
                  <Box
                    sx={{
                      p: 1.75,
                      mb: 2.5,
                      borderRadius: '10px',
                      bgcolor: '#f0f7ff',
                      border: '1px solid #dbeafe',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5
                    }}
                  >
                    <InfoOutlined sx={{ color: '#2563eb', fontSize: 20, flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ fontSize: '0.84rem', color: '#475569', lineHeight: 1.5 }}>
                      <Box component="span" sx={{ fontWeight: 700, color: '#1e3a8a' }}>
                        Booked Occupancy: {room.adultsCount || 1} Adult{(room.adultsCount || 1) > 1 ? 's' : ''}{(room.kidsCount || 0) > 0 ? `, ${room.kidsCount} Child${room.kidsCount > 1 ? 'ren' : ''}` : ''} ({room.guests.length} Guest{room.guests.length > 1 ? 's' : ''} Total)
                      </Box>
                      {' — Only Primary Guest check-in details are required. Accompanying guest names and IDs are optional at this step.'}
                    </Typography>
                  </Box>

                  {/* Guests list */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {room.guests.map((guest, gIdx) => {
                      const isPrimary = guest.isPrimary;
                      const hasId = Boolean(
                        guest.idDocumentUrl &&
                        guest.idDocumentUrl !== 'null' &&
                        guest.idDocumentUrl !== 'undefined'
                      );
                      const hasName = Boolean(guest.name && guest.name.trim() !== '');

                      return (
                        <Box
                          key={guest.id || gIdx}
                          onClick={() => setActiveModalGuest({ rIdx, gIdx })}
                          sx={{
                            p: 2,
                            borderRadius: '12px',
                            border: '1px solid #e5e7eb',
                            bgcolor: '#ffffff',
                            transition: 'all 0.2s ease',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            flexWrap: 'wrap',
                            gap: 2,
                            '&:hover': {
                              borderColor: '#cbd5e1',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          {/* Guest info left */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box
                              sx={{
                                width: 44,
                                height: 44,
                                borderRadius: '50%',
                                bgcolor: '#0f172a',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ffffff',
                                flexShrink: 0
                              }}
                            >
                              <Person sx={{ fontSize: 22 }} />
                            </Box>
                            <Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '0.92rem' }}>
                                  Guest #{gIdx + 1}: {hasName ? guest.name : (isPrimary ? 'Primary Guest (Not Entered)' : 'Accompanying Guest')}
                                </Typography>
                                <Box
                                  component="span"
                                  sx={{
                                    bgcolor: isPrimary ? '#ede9fe' : '#f1f5f9',
                                    color: isPrimary ? '#4f46e5' : '#475569',
                                    borderRadius: '6px',
                                    px: 1,
                                    py: 0.2,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    display: 'inline-block'
                                  }}
                                >
                                  {isPrimary ? 'Primary Guest' : `Accompanying • ${guest.guestType || 'Adult'}`}
                                </Box>
                                <Box
                                  component="span"
                                  sx={{
                                    bgcolor: '#f1f5f9',
                                    color: '#475569',
                                    borderRadius: '6px',
                                    px: 1,
                                    py: 0.2,
                                    fontSize: '0.7rem',
                                    fontWeight: 600,
                                    display: 'inline-block'
                                  }}
                                >
                                  {guest.nationality || (guest.isIndian ? 'Indian' : (guest.country || 'Non-Indian'))}
                                </Box>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4, flexWrap: 'wrap' }}>
                                {guest.email && (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#64748b', fontSize: '0.78rem' }}>
                                    <MailOutline sx={{ fontSize: 14, color: '#9ca3af' }} />
                                    {guest.email}
                                  </Box>
                                )}
                                {guest.email && (guest.phone || guest.mobile) && (
                                  <Box component="span" sx={{ color: '#cbd5e1', fontSize: '0.75rem' }}>•</Box>
                                )}
                                {(guest.phone || guest.mobile) && (
                                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: '#64748b', fontSize: '0.78rem' }}>
                                    <PhoneOutlined sx={{ fontSize: 14, color: '#9ca3af' }} />
                                    {guest.phone || guest.mobile}
                                  </Box>
                                )}
                                {!guest.email && !guest.phone && !guest.mobile && (
                                  <Typography sx={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                                    {isPrimary ? 'Required contact & ID details' : 'Optional ID & check-in details'}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>

                          {/* Guest action right */}
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {hasId ? (
                              <Box
                                sx={{
                                  bgcolor: '#ecfdf5',
                                  border: '1px solid #a7f3d0',
                                  color: '#059669',
                                  borderRadius: '9999px',
                                  px: 1.5,
                                  py: 0.4,
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}
                              >
                                <CheckCircle sx={{ fontSize: 14, color: '#059669' }} />
                                ID Uploaded
                              </Box>
                            ) : hasName ? (
                              <Box
                                sx={{
                                  bgcolor: '#ecfdf5',
                                  border: '1px solid #a7f3d0',
                                  color: '#059669',
                                  borderRadius: '9999px',
                                  px: 1.5,
                                  py: 0.4,
                                  fontSize: '0.75rem',
                                  fontWeight: 600,
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}
                              >
                                <CheckCircle sx={{ fontSize: 14, color: '#059669' }} />
                                Name Entered
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  bgcolor: isPrimary ? '#fffbeb' : '#f8fafc',
                                  border: isPrimary ? '1px solid #fde68a' : '1px solid #e2e8f0',
                                  color: isPrimary ? '#d97706' : '#64748b',
                                  borderRadius: '9999px',
                                  px: 1.5,
                                  py: 0.4,
                                  fontSize: '0.75rem',
                                  fontWeight: 600
                                }}
                              >
                                {isPrimary ? 'Action Required' : 'Optional'}
                              </Box>
                            )}

                            {!isPrimary && (
                              <Tooltip title="Set as Primary Guest for this Reservation">
                                <IconButton
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSetPrimaryGuest(rIdx, gIdx);
                                  }}
                                  sx={{ color: '#94a3b8', '&:hover': { color: '#4f46e5', bgcolor: '#f5f3ff' } }}
                                >
                                  <StarBorder fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}

                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<EditOutlined sx={{ fontSize: 15 }} />}
                              onClick={(e) => {
                                e.stopPropagation();
                                setActiveModalGuest({ rIdx, gIdx });
                              }}
                              sx={{
                                borderRadius: '10px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                borderColor: '#e5e7eb',
                                color: '#0f172a',
                                bgcolor: '#ffffff',
                                px: 1.75,
                                py: 0.6,
                                '&:hover': {
                                  bgcolor: '#f8fafc',
                                  borderColor: '#cbd5e1'
                                }
                              }}
                            >
                              {hasName ? 'Edit Details' : 'Enter Details'}
                            </Button>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </CardContent>
              </Card>
            ))}

            {/* Terms and Conditions Card */}
            <Box
              sx={{
                bgcolor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                p: { xs: 2, sm: 2.25 },
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25
              }}
            >
              <Checkbox
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                required
                sx={{
                  color: '#4f46e5',
                  '&.Mui-checked': {
                    color: '#4f46e5'
                  },
                  p: 0.5
                }}
              />
              <Typography variant="body2" sx={{ fontSize: '0.82rem', color: '#475569', lineHeight: 1.5 }}>
                I agree to the Hotel's{' '}
                <Box component="span" sx={{ textDecoration: 'underline', color: '#4f46e5', fontWeight: 600, cursor: 'pointer' }}>
                  Terms & Conditions
                </Box>
                {', '}
                <Box component="span" sx={{ textDecoration: 'underline', color: '#4f46e5', fontWeight: 600, cursor: 'pointer' }}>
                  Privacy Policy
                </Box>
                {', and certify that all guest identification information provided is accurate and complete under local hospitality guidelines. '}
                <Box component="span" sx={{ color: '#ef4444', fontWeight: 700 }}>*</Box>
              </Typography>
            </Box>

            {/* Submit Button */}
            <Button
              type="submit"
              fullWidth
              disabled={submitting}
              sx={{
                bgcolor: '#4f46e5',
                color: '#ffffff',
                py: 1.6,
                borderRadius: '12px',
                fontSize: '0.95rem',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: '0 4px 14px 0 rgba(79, 70, 229, 0.35)',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: '#4338ca',
                  boxShadow: '0 6px 18px 0 rgba(79, 70, 229, 0.45)'
                },
                '&:disabled': {
                  bgcolor: '#a5b4fc',
                  color: '#ffffff'
                }
              }}
            >
              {submitting ? (
                <CircularProgress size={24} sx={{ color: '#ffffff' }} />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                  Complete Pre-Arrival Registration for All Guests
                  <ArrowForward sx={{ fontSize: 18 }} />
                </Box>
              )}
            </Button>
          </form>

          {/* Trust badges footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: { xs: 2, sm: 3.5 },
              mt: 3,
              mb: 6
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#64748b', fontSize: '0.78rem', fontWeight: 500 }}>
              <VerifiedUserOutlined sx={{ fontSize: 16, color: '#9ca3af' }} />
              Official Secure Check-In
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#64748b', fontSize: '0.78rem', fontWeight: 500 }}>
              <QrCode2 sx={{ fontSize: 16, color: '#9ca3af' }} />
              Direct Mobile Key & QR Access
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: '#64748b', fontSize: '0.78rem', fontWeight: 500 }}>
              <SupportAgentOutlined sx={{ fontSize: 16, color: '#9ca3af' }} />
              24/7 Front Desk Concierge Support
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Guest Details Modal Dialog */}
      {(() => {
        if (!activeModalGuest) return null;
        const { rIdx, gIdx } = activeModalGuest;
        const room = roomsData[rIdx];
        const guest = room?.guests?.[gIdx];
        if (!guest) return null;

        const isUploading = Boolean(uploadingMap[`${rIdx}_${gIdx}`]);
        const totalGuests = room.guests.length;
        const hasModalId = Boolean(
          guest.idDocumentUrl &&
          guest.idDocumentUrl !== 'null' &&
          guest.idDocumentUrl !== 'undefined'
        );
        const isPdf = Boolean(
          guest.idDocumentUrl?.toLowerCase().includes('.pdf') ||
          guest.idDocumentName?.toLowerCase().endsWith('.pdf') ||
          guest.idDocumentUrl?.startsWith('data:application/pdf')
        );

        return (
          <Dialog
            open={Boolean(activeModalGuest)}
            onClose={() => setActiveModalGuest(null)}
            maxWidth="md"
            fullWidth
            PaperProps={{
              sx: {
                borderRadius: '16px',
                p: { xs: 1, sm: 2 },
                bgcolor: '#ffffff',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
              }
            }}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1.5, borderBottom: '1px solid #f1f5f9' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '10px',
                    bgcolor: guest.isPrimary ? '#4f46e5' : '#f1f5f9',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: guest.isPrimary ? '#ffffff' : '#64748b'
                  }}
                >
                  {guest.isPrimary ? <Star /> : <Person />}
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', color: '#0f172a' }}>
                      Guest #{gIdx + 1} Details
                    </Typography>
                    <Chip
                      label={guest.isPrimary ? 'Primary Guest' : `Accompanying • ${guest.guestType || 'Adult'}`}
                      size="small"
                      sx={{
                        bgcolor: guest.isPrimary ? '#ede9fe' : '#f1f5f9',
                        color: guest.isPrimary ? '#4f46e5' : '#475569',
                        fontWeight: 600,
                        height: 22,
                        fontSize: '0.7rem'
                      }}
                    />
                  </Box>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Room {rIdx + 1}: {room.roomTypeName || room.roomType || `Room ${rIdx + 1}`}
                  </Typography>
                </Box>
              </Box>
              <IconButton onClick={() => setActiveModalGuest(null)} sx={{ color: '#94a3b8', '&:hover': { color: '#0f172a' } }}>
                <Close />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: 3, pb: 2 }}>
              {/* Origin toggle */}
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  borderRadius: '12px',
                  bgcolor: '#f8fafc',
                  border: '1px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.88rem', color: '#0f172a' }}>
                    Guest Nationality / Origin
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Specify whether this guest is an Indian national or an international traveler
                  </Typography>
                </Box>
                <ToggleButtonGroup
                  value={guest.isIndian ? 'indian' : 'non-indian'}
                  exclusive
                  onChange={(e, val) => {
                    if (val !== null) {
                      const isInd = val === 'indian';
                      handleGuestChange(rIdx, gIdx, 'isIndian', isInd);
                      if (isInd) {
                        handleGuestChange(rIdx, gIdx, 'country', 'India');
                        handleGuestChange(rIdx, gIdx, 'nationality', 'Indian');
                        handleGuestChange(rIdx, gIdx, 'birthCountry', 'India');
                        if (guest.idType === 'Passport' || !guest.idType) {
                          handleGuestChange(rIdx, gIdx, 'idType', 'Aadhar');
                        }
                      } else {
                        if (guest.country === 'India') handleGuestChange(rIdx, gIdx, 'country', '');
                        if (guest.nationality === 'Indian') handleGuestChange(rIdx, gIdx, 'nationality', '');
                        if (guest.birthCountry === 'India') handleGuestChange(rIdx, gIdx, 'birthCountry', '');
                        if (guest.idType === 'Aadhar') {
                          handleGuestChange(rIdx, gIdx, 'idType', 'Passport');
                        }
                      }
                    }
                  }}
                  size="small"
                  sx={{
                    bgcolor: '#ffffff',
                    borderRadius: '10px',
                    p: 0.5,
                    border: '1px solid #e2e8f0',
                    '& .MuiToggleButton-root': {
                      borderRadius: '8px !important',
                      px: 2,
                      py: 0.5,
                      fontWeight: 600,
                      textTransform: 'none',
                      fontSize: '0.8rem',
                      border: 'none',
                      color: '#64748b',
                      '&.Mui-selected': {
                        bgcolor: '#4f46e5',
                        color: '#ffffff',
                        boxShadow: '0 2px 6px rgba(79, 70, 229, 0.3)',
                        '&:hover': {
                          bgcolor: '#4338ca'
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="indian">Indian</ToggleButton>
                  <ToggleButton value="non-indian">Non-Indian</ToggleButton>
                </ToggleButtonGroup>
              </Box>

              <Typography sx={{ fontWeight: 700, color: '#4f46e5', fontSize: '0.82rem', mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                1. Personal Details
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={2}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Title"
                    value={guest.title || ''}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'title', e.target.value)}
                    SelectProps={{ native: true }}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  >
                    <option value=""></option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Dr">Dr</option>
                    <option value="Prof">Prof</option>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Full Name"
                    required={guest.isPrimary}
                    placeholder={guest.isPrimary ? "Enter primary guest full name" : "Enter accompanying guest name (optional)"}
                    value={guest.name || ''}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'name', e.target.value)}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Guest Type"
                    value={guest.guestType || 'Adult'}
                    InputProps={{ readOnly: true }}
                    sx={{ bgcolor: '#f8fafc', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    size="small"
                    type="email"
                    label="Email Address"
                    required={guest.isPrimary}
                    placeholder={guest.isPrimary ? "Primary email required" : "Optional for accompanying guest"}
                    value={guest.email || ''}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'email', e.target.value)}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 85, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                      <InputLabel>Code</InputLabel>
                      <Select
                        value={guest.phoneCode || (guest.isIndian ? '+91' : '+971')}
                        label="Code"
                        onChange={(e) => handleGuestChange(rIdx, gIdx, 'phoneCode', e.target.value)}
                      >
                        {SUPPORTED_PHONE_CODES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      size="small"
                      label="Phone Number"
                      required={guest.isPrimary}
                      value={guest.phone || ''}
                      onChange={(e) => handleGuestChange(rIdx, gIdx, 'phone', e.target.value.replace(/\D/g, '').slice(0, 15))}
                      sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 85, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}>
                      <InputLabel>Code</InputLabel>
                      <Select
                        value={guest.mobilePhoneCode || (guest.isIndian ? '+91' : '+971')}
                        label="Code"
                        onChange={(e) => handleGuestChange(rIdx, gIdx, 'mobilePhoneCode', e.target.value)}
                      >
                        {SUPPORTED_PHONE_CODES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField
                      fullWidth
                      size="small"
                      label="Mobile Number"
                      value={guest.mobile || ''}
                      onChange={(e) => handleGuestChange(rIdx, gIdx, 'mobile', e.target.value.replace(/\D/g, '').slice(0, 15))}
                      sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Gender"
                    value={guest.gender || ''}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'gender', e.target.value)}
                    SelectProps={{ native: true }}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  >
                    <option value=""></option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={guest.isIndian ? 6 : 4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="Date of Birth"
                    InputLabelProps={{ shrink: true }}
                    value={guest.birthDate || ''}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'birthDate', e.target.value)}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>

                {!guest.isIndian && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Country of Birth"
                      value={guest.birthCountry || ''}
                      onChange={(e) => handleGuestChange(rIdx, gIdx, 'birthCountry', e.target.value)}
                      sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                  </Grid>
                )}

                {!guest.isIndian && (
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Nationality"
                      value={guest.nationality || ''}
                      onChange={(e) => handleGuestChange(rIdx, gIdx, 'nationality', e.target.value)}
                      sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Residential Address"
                    value={guest.address || ''}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'address', e.target.value)}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>

                {!guest.isIndian && (
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="City"
                      value={guest.city || ''}
                      onChange={(e) => handleGuestChange(rIdx, gIdx, 'city', e.target.value)}
                      sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={guest.isIndian ? 6 : 3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="State / Province"
                    value={guest.state || ''}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'state', e.target.value)}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>

                {!guest.isIndian && (
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label="Country"
                      value={guest.country || ''}
                      onChange={(e) => handleGuestChange(rIdx, gIdx, 'country', e.target.value)}
                      sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={guest.isIndian ? 6 : 3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Postal / Zip Code"
                    value={guest.zip || ''}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'zip', e.target.value)}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>
              </Grid>

              <Typography sx={{ fontWeight: 700, color: '#4f46e5', fontSize: '0.82rem', mb: 2, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                2. Identity & Verification
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="ID Document Type"
                    value={guest.idType || (guest.isIndian ? 'Aadhar' : 'Passport')}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'idType', e.target.value)}
                    SelectProps={{ native: true }}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  >
                    {guest.isIndian ? (
                      <>
                        <option value="Aadhar">Aadhar</option>
                        <option value="Passport">Passport</option>
                        <option value="Driving License">Driving License</option>
                        <option value="Voter ID">Voter ID</option>
                        <option value="National ID">National ID</option>
                      </>
                    ) : (
                      <>
                        <option value="Passport">Passport</option>
                        <option value="National ID">National ID</option>
                        <option value="Driver License">Driver License</option>
                      </>
                    )}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label="ID / Passport Number"
                    value={guest.idProof || ''}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'idProof', e.target.value)}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    size="small"
                    type="date"
                    label="ID Expiry Date"
                    InputLabelProps={{ shrink: true }}
                    value={guest.idExpiryDate || ''}
                    onChange={(e) => handleGuestChange(rIdx, gIdx, 'idExpiryDate', e.target.value)}
                    sx={{ bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
                  />
                </Grid>

                <Grid item xs={12}>
                  {hasModalId ? (
                    <Box
                      sx={{
                        mt: 1,
                        p: 2,
                        border: '1.5px solid #bbf7d0',
                        borderRadius: '12px',
                        bgcolor: '#f0fdf4',
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 2
                      }}
                    >
                      {/* Left: Thumbnail & Document Info */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, width: { xs: '100%', sm: 'auto' } }}>
                        {isPdf ? (
                          <Box
                            onClick={() => window.open(guest.idDocumentUrl, '_blank')}
                            title="Click to view PDF in new tab"
                            sx={{
                              width: 80,
                              height: 70,
                              borderRadius: '10px',
                              bgcolor: '#fee2e2',
                              border: '1px solid #fca5a5',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              flexShrink: 0,
                              transition: 'all 0.2s',
                              '&:hover': { transform: 'scale(1.03)', boxShadow: '0 4px 12px rgba(220, 38, 38, 0.15)' }
                            }}
                          >
                            <PictureAsPdf sx={{ fontSize: 32, color: '#dc2626' }} />
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#b91c1c' }}>PDF</Typography>
                          </Box>
                        ) : (
                          <Box
                            onClick={() => setPreviewImageUrl(guest.idDocumentUrl)}
                            title="Click to zoom preview"
                            sx={{
                              position: 'relative',
                              width: 86,
                              height: 70,
                              borderRadius: '10px',
                              overflow: 'hidden',
                              border: '1.5px solid #86efac',
                              bgcolor: '#ffffff',
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
                              cursor: 'pointer',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              '&:hover .zoom-overlay': { opacity: 1 },
                              '&:hover img': { transform: 'scale(1.05)' }
                            }}
                          >
                            <Box
                              component="img"
                              src={guest.idDocumentUrl}
                              alt="ID Preview"
                              sx={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                transition: 'transform 0.2s ease',
                                display: 'block'
                              }}
                            />
                            <Box
                              className="zoom-overlay"
                              sx={{
                                position: 'absolute',
                                inset: 0,
                                bgcolor: 'rgba(15, 23, 42, 0.5)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#ffffff',
                                opacity: 0,
                                transition: 'opacity 0.2s ease'
                              }}
                            >
                              <Visibility sx={{ fontSize: 20 }} />
                            </Box>
                          </Box>
                        )}

                        {/* Text info */}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
                            <Chip
                              icon={<CheckCircle sx={{ fontSize: '13px !important', color: '#16a34a !important' }} />}
                              label="ID Attached"
                              size="small"
                              sx={{
                                bgcolor: '#dcfce7',
                                color: '#15803d',
                                fontWeight: 700,
                                fontSize: '0.72rem',
                                height: 22
                              }}
                            />
                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 500 }}>
                              {isPdf ? 'PDF Document' : 'Image Document'}
                            </Typography>
                          </Box>

                          <Typography
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.88rem',
                              color: '#0f172a',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: { xs: 200, sm: 260, md: 340 }
                            }}
                            title={guest.idDocumentName || 'Identity Document'}
                          >
                            {guest.idDocumentName || 'Identity Document'}
                          </Typography>

                          <Typography variant="caption" sx={{ color: '#15803d', display: 'block', mt: 0.25, fontWeight: 500 }}>
                            ✓ Click thumbnail to view full resolution
                          </Typography>
                        </Box>
                      </Box>

                      {/* Right: Actions */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Visibility sx={{ fontSize: 16 }} />}
                          onClick={() => {
                            if (isPdf) {
                              window.open(guest.idDocumentUrl, '_blank');
                            } else {
                              setPreviewImageUrl(guest.idDocumentUrl);
                            }
                          }}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: '#cbd5e1',
                            color: '#334155',
                            bgcolor: '#ffffff',
                            '&:hover': {
                              borderColor: '#94a3b8',
                              bgcolor: '#f8fafc'
                            }
                          }}
                        >
                          Preview
                        </Button>

                        <input
                          accept="image/*,.pdf"
                          style={{ display: 'none' }}
                          id={`id-upload-modal-${rIdx}-${gIdx}`}
                          type="file"
                          onChange={(e) => handleFileUpload(e, rIdx, gIdx)}
                        />
                        <label htmlFor={`id-upload-modal-${rIdx}-${gIdx}`}>
                          <Button
                            variant="outlined"
                            component="span"
                            disabled={isUploading}
                            size="small"
                            startIcon={isUploading ? <CircularProgress size={14} /> : <CloudUpload sx={{ fontSize: 16 }} />}
                            sx={{
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 600,
                              borderColor: '#3b49df',
                              color: '#3b49df',
                              bgcolor: '#ffffff',
                              '&:hover': {
                                borderColor: '#2f3ab2',
                                bgcolor: 'rgba(59, 73, 223, 0.04)'
                              }
                            }}
                          >
                            {isUploading ? 'Uploading...' : 'Replace'}
                          </Button>
                        </label>

                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
                          onClick={() => handleRemoveFile(rIdx, gIdx)}
                          sx={{
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: '#ffffff',
                            borderColor: '#fca5a5',
                            color: '#dc2626',
                            '&:hover': {
                              borderColor: '#ef4444',
                              bgcolor: '#fef2f2'
                            }
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        mt: 1,
                        p: 2.5,
                        border: '2px dashed #cbd5e1',
                        borderRadius: '12px',
                        bgcolor: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '12px',
                            bgcolor: '#3b49df',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#ffffff',
                            flexShrink: 0
                          }}
                        >
                          <CloudUpload sx={{ fontSize: 24, color: '#ffffff' }} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: 600, fontSize: '0.88rem', color: '#0f172a' }}>
                            Upload Identity Proof (Optional / Quick Check-in)
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                            Supported formats: PDF, JPG, PNG (Max size: 5MB)
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <input
                          accept="image/*,.pdf"
                          style={{ display: 'none' }}
                          id={`id-upload-modal-${rIdx}-${gIdx}`}
                          type="file"
                          onChange={(e) => handleFileUpload(e, rIdx, gIdx)}
                        />
                        <label htmlFor={`id-upload-modal-${rIdx}-${gIdx}`}>
                          <Button
                            variant="outlined"
                            component="span"
                            disabled={isUploading}
                            startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUpload />}
                            sx={{
                              borderRadius: '8px',
                              textTransform: 'none',
                              fontWeight: 600,
                              borderColor: '#3b49df',
                              color: '#3b49df',
                              bgcolor: '#ffffff',
                              '&:hover': {
                                borderColor: '#2f3ab2',
                                bgcolor: 'rgba(59, 73, 223, 0.04)'
                              }
                            }}
                          >
                            {isUploading ? 'Uploading...' : 'Upload ID'}
                          </Button>
                        </label>
                      </Box>
                    </Box>
                  )}
                </Grid>

              </Grid>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  disabled={gIdx === 0}
                  onClick={() => setActiveModalGuest({ rIdx, gIdx: gIdx - 1 })}
                  startIcon={<ArrowBackIosNew sx={{ fontSize: '13px !important' }} />}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', color: '#64748b' }}
                >
                  Previous Guest
                </Button>
                <Button
                  disabled={gIdx === totalGuests - 1}
                  onClick={() => setActiveModalGuest({ rIdx, gIdx: gIdx + 1 })}
                  endIcon={<ArrowForwardIos sx={{ fontSize: '13px !important' }} />}
                  sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', color: '#64748b' }}
                >
                  Next Guest
                </Button>
              </Box>
              <Button
                variant="contained"
                onClick={() => setActiveModalGuest(null)}
                sx={{
                  px: 3,
                  py: 1,
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 600,
                  bgcolor: '#4f46e5',
                  boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)',
                  '&:hover': {
                    bgcolor: '#4338ca'
                  }
                }}
              >
                Save & Close
              </Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* ID Document Full Preview Lightbox Modal */}
      <Dialog
        open={Boolean(previewImageUrl)}
        onClose={() => setPreviewImageUrl(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            overflow: 'hidden',
            bgcolor: '#0f172a'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            py: 2,
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
            bgcolor: '#1e293b',
            color: '#ffffff'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Visibility sx={{ color: '#818cf8', fontSize: 22 }} />
            <Typography variant="subtitle1" fontWeight={700} color="#ffffff">
              ID Document Full Preview
            </Typography>
          </Box>
          <IconButton onClick={() => setPreviewImageUrl(null)} size="small" sx={{ color: '#94a3b8', '&:hover': { color: '#ffffff' } }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            p: 3,
            bgcolor: '#0f172a',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 350
          }}
        >
          {previewImageUrl && (
            <Box
              component="img"
              src={previewImageUrl}
              alt="ID Document Full Preview"
              sx={{
                maxWidth: '100%',
                maxHeight: '75vh',
                objectFit: 'contain',
                borderRadius: '8px',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)'
              }}
            />
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            py: 1.5,
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            bgcolor: '#1e293b',
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <Button
            variant="text"
            size="small"
            onClick={() => window.open(previewImageUrl, '_blank')}
            sx={{ textTransform: 'none', fontWeight: 600, color: '#818cf8' }}
          >
            Open Original in New Tab
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => setPreviewImageUrl(null)}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              bgcolor: '#4f46e5',
              borderRadius: '8px',
              '&:hover': { bgcolor: '#4338ca' }
            }}
          >
            Close Preview
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          borderTop: '1px solid #e5e7eb',
          bgcolor: '#ffffff',
          py: 2.5,
          px: { xs: 2, sm: 4, md: 6 },
          mt: 'auto',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2
        }}
      >
        <Typography sx={{ color: '#64748b', fontSize: '0.78rem' }}>
          © {new Date().getFullYear()} {hotelName}. All rights reserved.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: '#64748b', fontSize: '0.78rem' }}>
          <Box component="span" sx={{ cursor: 'pointer', '&:hover': { color: '#0f172a' } }}>
            House Rules
          </Box>
          <Box component="span" sx={{ color: '#cbd5e1' }}>•</Box>
          <Box component="span" sx={{ cursor: 'pointer', '&:hover': { color: '#0f172a' } }}>
            Privacy Policy
          </Box>
          <Box component="span" sx={{ color: '#cbd5e1' }}>•</Box>
          <Box component="span">
            Reception: {summary?.hotelPhone || '+971 4 800 2473'}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default GuestWebCheckin;
